// ═══════════════════════════════════════════════════════════════
// WHAT'S NEW SIDEBAR — Announcements for new features / workflows
// Shows entries from src/constants/whatsNew.js (newest first).
// A subtle pulse appears on the toolbar button until the latest
// entry has been opened at least once (tracked in localStorage).
// Path: src/components/features/WhatsNewSidebar.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, ChevronRight, ArrowRight, Pin, BookOpen } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { WHATS_NEW } from "../../constants/whatsNew";
import { GuideReaderModal } from "../ui/GuideReaderModal";
import { getGuide } from "../../constants/guides";

const SEEN_KEY = "cv_whatsnew_seen_id";

// ── Helpers ────────────────────────────────────────────────────
const getSeenId = () => {
  try {
    return localStorage.getItem(SEEN_KEY) || null;
  } catch {
    return null;
  }
};

const setSeenId = (id) => {
  try {
    if (id) localStorage.setItem(SEEN_KEY, id);
  } catch {
    /* ignore storage errors */
  }
};

// Newest by date, not by array index, so pinned-first ordering
// doesn't confuse the "has unread" pulse.
const latestEntry = () => {
  if (!WHATS_NEW.length) return null;
  return [...WHATS_NEW].sort((a, b) =>
    (b.date || "").localeCompare(a.date || "")
  )[0];
};

/** Returns true when the latest entry has NOT been seen yet. */
export const useHasUnreadWhatsNew = () => {
  const [seenId, setSeenIdState] = useState(getSeenId);
  useEffect(() => {
    setSeenIdState(getSeenId());
  }, []);
  const latest = latestEntry();
  return !!(latest && latest.id !== seenId);
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

// ═══════════════════════════════════════════════════════════════
// MAIN SIDEBAR COMPONENT
// ═══════════════════════════════════════════════════════════════
export const WhatsNewSidebar = ({ isOpen, onClose }) => {
  const { palette, navigate } = useApp();
  const panelRef = useRef(null);
  const [activeGuide, setActiveGuide] = useState(null);

  // Pinned entries float to the top; the rest preserve WHATS_NEW order
  // (which is already newest-first).
  const sortedEntries = React.useMemo(() => {
    const pinned = WHATS_NEW.filter((e) => e.pinned);
    const rest = WHATS_NEW.filter((e) => !e.pinned);
    return [...pinned, ...rest];
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const latest = latestEntry();
    if (latest) setSeenId(latest.id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const onBackdropClick = (e) => {
    if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
  };

  const handleCta = (view, openHelp = false) => {
    if (!view) return;
    onClose();
    if (typeof navigate === "function") {
      navigate(view, null, openHelp ? { openHelp: true } : null);
    }
  };

  const handleOpenGuide = (guideId) => {
    const guide = getGuide(guideId);
    if (!guide) return;
    setActiveGuide(guide);
  };

  const bannerBg = "#0b1220";
  const accent = palette.accent1 || palette.accent2 || "#6366f1";

  return (
    <>
      <div
        onClick={onBackdropClick}
        style={{
          position: "fixed",
          inset: 0,
          background: isOpen ? "rgba(0,0,0,0.25)" : "transparent",
          zIndex: 1100,
          pointerEvents: isOpen ? "auto" : "none",
          transition: "background 0.25s ease",
        }}
      />
      <div
        ref={panelRef}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: 380,
          maxWidth: "92vw",
          height: "100vh",
          background: "rgba(255,255,255,0.97)",
          backdropFilter: "blur(12px)",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
          zIndex: 1101,
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          flexDirection: "column",
          fontFamily: FONT_FAMILY,
          overflowY: "auto",
        }}
      >
        {/* Branded banner */}
        <div
          style={{
            background: bannerBg,
            padding: "26px 20px 22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: -40,
              right: -40,
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.12)",
              filter: "blur(4px)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              position: "relative",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: "rgba(255,255,255,0.18)",
                border: "1px solid rgba(255,255,255,0.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Sparkles size={ICON_SIZES.lg} color="#fff" />
            </div>
            <div
              style={{
                fontSize: 26,
                color: "#fff",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                textShadow: "0 1px 2px rgba(0,0,0,0.18)",
              }}
            >
              What's New
            </div>
          </div>
          <button
            onClick={onClose}
            title="Close"
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8,
              padding: "6px 8px",
              cursor: "pointer",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              lineHeight: 1,
              flexShrink: 0,
              position: "relative",
            }}
          >
            <X size={ICON_SIZES.sm} />
          </button>
        </div>

        {/* Entries */}
        <div style={{ padding: "16px 20px 24px", flex: 1 }}>
          {sortedEntries.length === 0 && (
            <div
              style={{
                padding: 20,
                fontSize: FONT_SIZES.sm,
                color: "#6b7280",
                textAlign: "center",
              }}
            >
              Nothing new to announce right now.
            </div>
          )}

          {sortedEntries.map((entry, idx) => (
            <div
              key={entry.id}
              style={{
                marginBottom: 20,
                padding: 16,
                borderRadius: 12,
                background: entry.pinned ? `${accent}08` : "#ffffff",
                border: entry.pinned
                  ? `2px solid ${accent}`
                  : `1px solid ${palette.cardBorder || "#e5e7eb"}`,
                boxShadow:
                  idx === 0
                    ? `0 0 0 2px ${accent}22`
                    : "0 1px 3px rgba(0,0,0,0.04)",
                position: "relative",
              }}
            >
              {entry.pinned && (
                <div
                  style={{
                    position: "absolute",
                    top: -10,
                    left: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "3px 10px",
                    borderRadius: 99,
                    background: accent,
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  <Pin size={ICON_SIZES.xs} /> PINNED
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                {entry.tag && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      padding: "3px 8px",
                      borderRadius: 6,
                      background: `${accent}18`,
                      color: accent,
                    }}
                  >
                    {entry.tag}
                  </span>
                )}
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: "#6b7280",
                    marginLeft: "auto",
                  }}
                >
                  {formatDate(entry.date)}
                </span>
              </div>

              <div
                style={{
                  fontSize: FONT_SIZES.lg,
                  fontWeight: 700,
                  color: "#111827",
                  marginBottom: 6,
                  lineHeight: 1.25,
                }}
              >
                {entry.title}
              </div>

              {entry.summary && (
                <div
                  style={{
                    fontSize: FONT_SIZES.sm,
                    color: "#374151",
                    lineHeight: 1.55,
                    marginBottom: entry.details?.length ? 10 : 12,
                  }}
                >
                  {entry.summary}
                </div>
              )}

              {(entry.details || []).map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <ChevronRight
                    size={ICON_SIZES.sm}
                    style={{
                      color: accent,
                      marginTop: 3,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: FONT_SIZES.sm,
                      color: "#374151",
                      lineHeight: 1.5,
                    }}
                  >
                    {d}
                  </span>
                </div>
              ))}

              {entry.ctaLabel && (entry.ctaView || entry.guideId) && (
                <button
                  onClick={() => {
                    if (entry.guideId) handleOpenGuide(entry.guideId);
                    else handleCta(entry.ctaView, entry.ctaOpenHelp);
                  }}
                  style={{
                    marginTop: 12,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "none",
                    background: accent,
                    color: "#fff",
                    fontSize: FONT_SIZES.sm,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {entry.ctaLabel}
                  {entry.guideId ? (
                    <BookOpen size={ICON_SIZES.sm} />
                  ) : (
                    <ArrowRight size={ICON_SIZES.sm} />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      {activeGuide && (
        <GuideReaderModal
          guide={activeGuide}
          onClose={() => setActiveGuide(null)}
        />
      )}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
// TOGGLE BUTTON — renders in ZoneOneHeader next to Help.
// Shows a red pulse/shimmer until the latest entry has been seen.
// ═══════════════════════════════════════════════════════════════
export const WhatsNewToggleButton = ({ onClick }) => {
  const hasUnread = useHasUnreadWhatsNew();
  const latest = latestEntry();
  if (!latest) return null;

  return (
    <>
      <style>{`
        @keyframes cv-whatsnew-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.85), 0 4px 14px rgba(220,38,38,0.45); }
          70%  { box-shadow: 0 0 0 14px rgba(220, 38, 38, 0), 0 4px 14px rgba(220,38,38,0.30); }
          100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0), 0 4px 14px rgba(220,38,38,0.45); }
        }
        @keyframes cv-whatsnew-sparkle {
          0%, 100% { transform: rotate(0deg) scale(1); opacity: 1; }
          50%      { transform: rotate(12deg) scale(1.2); opacity: 0.9; }
        }
        @keyframes cv-whatsnew-shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
      <button
        onClick={onClick}
        title={hasUnread ? `New: ${latest.title}` : "What's New in ClearView"}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 18px",
          borderRadius: 10,
          border: "2px solid #7f1d1d",
          background: hasUnread
            ? "linear-gradient(135deg, #dc2626 0%, #ef4444 50%, #dc2626 100%)"
            : "#b91c1c",
          backgroundSize: hasUnread ? "200% 100%" : undefined,
          color: "#ffffff",
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: "0.01em",
          fontFamily: FONT_FAMILY,
          cursor: "pointer",
          transition: "transform 0.15s, filter 0.15s",
          animation: hasUnread
            ? "cv-whatsnew-pulse 1.8s ease-out infinite, cv-whatsnew-shimmer 3s linear infinite"
            : "none",
          position: "relative",
          textShadow: "0 1px 2px rgba(0,0,0,0.35)",
          boxShadow: hasUnread ? undefined : "0 2px 6px rgba(127,29,29,0.4)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.filter = "brightness(1.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.filter = "brightness(1)";
        }}
      >
        <Sparkles
          size={ICON_SIZES.md}
          strokeWidth={2.5}
          style={{
            color: "#ffffff",
            animation: hasUnread
              ? "cv-whatsnew-sparkle 1.6s ease-in-out infinite"
              : "none",
            filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.3))",
          }}
        />
        <span>What's New</span>
      </button>
    </>
  );
};
